import { Box, Stack, Typography, useTheme } from '@mui/material'
import { format as dateFnsFormat } from 'date-fns'
import { ru } from 'date-fns/locale'
import { FC, useMemo } from 'react'
import { ReplyContentTextDTO, ReplyScheduleDTO } from '../../../api/gen/base'
import CalendarIcon from '../../../assets/icons/Calendar.svg'
import ChatIcon from '../../../assets/icons/Chat.svg?react'
import RefreshClockIcon from '../../../assets/icons/RefreshClock.svg'
import {
    NumberPostfixConfig,
    NumberWithCorrectPostfix,
} from '../../../components/NumberWithCorrectPostfix'
import { RegularRequestActionsMenu } from './RegularRequestActionsMenu'

const postfix: NumberPostfixConfig = {
    endsWithOne: 'вложение',
    endsBetweenTwoAndFour: 'вложения',
    other: 'вложений',
}

type Props = {
    request: ReplyScheduleDTO
    onEdit: (request: ReplyScheduleDTO) => void
    onDelete: (replyScheduleId: number) => void
}

export const RegularRequestItem: FC<Props> = ({ request, onEdit, onDelete }) => {
    const { breakpoints } = useTheme()

    const {
        reply: { content },
        timing,
    } = request

    const text = content.find(({ content_type }) => content_type === 'TEXT') as
        | ReplyContentTextDTO
        | undefined

    const attachmentCount = content.length - 1

    const scheduleData = useMemo(() => {
        if (timing.repeat === 'EVERYDAY') {
            return [
                {
                    Icon: CalendarIcon,
                    content: dateFnsFormat(timing.send_time, 'd MMMM, yyyy', { locale: ru }),
                },
                {
                    Icon: RefreshClockIcon,
                    content: `Ежедневно / ${dateFnsFormat(timing.send_time, 'HH:mm', { locale: ru })}`,
                },
            ]
        }

        if (timing.repeat === 'WEEK_DAYS') {
            return [
                {
                    Icon: CalendarIcon,
                    content: dateFnsFormat(timing.send_time, 'd MMMM, yyyy', { locale: ru }),
                },
                {
                    Icon: RefreshClockIcon,
                    content: `По дням недели / ${dateFnsFormat(timing.send_time, 'HH:mm', { locale: ru })}`,
                },
            ]
        }

        return [
            {
                Icon: CalendarIcon,
                content: dateFnsFormat(timing.send_time, 'd MMMM, yyyy / HH:mm', { locale: ru }),
            },
            {
                Icon: RefreshClockIcon,
                content: 'Не повторять',
            },
        ]
    }, [timing])

    return (
        <Stack
            sx={{
                p: '12px 6px',
                gap: 1,
                borderRadius: '16px',
                border: '1px solid #E7E8EE',

                ':hover': {
                    borderRadius: '16px',
                },
            }}>
            <Stack direction="row" alignItems="center" gap="6px">
                <Box
                    sx={{
                        m: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <ChatIcon color="#006CFD" width={24} height={24} />
                </Box>
                <Stack
                    direction="column"
                    sx={{
                        overflow: 'hidden',
                        ':hover': {
                            borderRadius: 0,
                        },
                    }}>
                    <Typography
                        variant="body2"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: '2',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {text?.text}
                    </Typography>
                    {attachmentCount > 0 && (
                        <Typography variant="caption2" color="#9195A1">
                            <NumberWithCorrectPostfix value={attachmentCount} postfix={postfix} />
                        </Typography>
                    )}
                </Stack>
                <RegularRequestActionsMenu request={request} onEdit={onEdit} onDelete={onDelete} />
            </Stack>
            <Stack
                sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    ml: '46px',

                    flexWrap: 'wrap',

                    [breakpoints.down('sm')]: {
                        ml: 0,
                    },
                }}>
                <Stack
                    sx={{
                        width: 'fit-content',
                        flexDirection: 'row',
                        alignItems: 'center',
                        border: '1px solid #E7E8EE',
                        borderRadius: '12px',
                        p: '6px',
                        gap: 1,
                        ':hover': {
                            borderRadius: '12px',
                        },
                    }}>
                    {scheduleData.map(({ Icon, content }, idx) => (
                        <Stack
                            key={idx}
                            sx={{
                                width: 'fit-content',
                                flexDirection: 'row',
                                alignItems: 'center',
                                backgroundColor: '#F4F4F6',
                                borderRadius: '8px',
                                gap: '6px',
                                p: '2px 6px 2px 4px',
                                ':hover': {
                                    borderRadius: '8px',
                                },
                            }}>
                            <Icon color="#9195A1" />
                            <Typography variant="caption2" color="#9195A1">
                                {content}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Stack>
    )
}
